import lodashGet from "lodash/get";
import { DateTime } from "luxon";
import React, { ComponentPropsWithoutRef } from "react";
import { PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { LuxonDateFormats } from "shared/datetime/dateFormats";
import { CreatedModifiedView } from "shared/mappers/database/created-modified/created-modified";

export type PillarTableColumnCreateModifiedProps<T> = {
  displayType?: "name" | "date" | "date-time" | "combined";
  placeholder?: string;
} & PillarTableBaseColumnCellValuePathProps<T> &
  ComponentPropsWithoutRef<"div">;

const PillarTableColumnCreateModified = <T extends Record<string, any>>({
  placeholder = "-",
  className,
  displayType = "combined",
  cellValuePath,
  ...props
}: PillarTableColumnCreateModifiedProps<T>) => {
  const { rowValue } = usePillarTableRowContext<T, any, any>();
  const createModified: CreatedModifiedView = lodashGet(
    rowValue,
    cellValuePath ?? "created",
  ) as CreatedModifiedView;
  if (!createModified) {
    return <>{placeholder}</>;
  }

  const timeStamp = DateTime.fromISO(createModified.timestamp, {
    zone: "America/Chicago",
  });
  let displayName = "";
  if (createModified.adminUser?.name) {
    displayName = createModified.adminUser.name;
  }
  if (createModified.societyUser?.firstName) {
    if (displayName !== "") {
      displayName +=
        " (" +
        createModified.societyUser.firstName +
        " " +
        createModified.societyUser.lastName +
        ")";
    } else {
      displayName =
        createModified.societyUser.firstName +
        " " +
        createModified.societyUser.lastName;
    }
  }

  if (displayType === "date" && timeStamp.isValid) {
    return <>{timeStamp.toFormat(LuxonDateFormats.D)}</>;
  }
  if (displayType === "date-time" && timeStamp.isValid) {
    return <>{timeStamp.toFormat(LuxonDateFormats.F)}</>;
  }
  if (displayType === "name" && displayName != "") {
    return <>{displayName}</>;
  }
  if (displayType === "combined" && timeStamp.isValid && displayName != "") {
    return (
      <>
        {displayName} ({timeStamp.toFormat(LuxonDateFormats.F)}
      </>
    );
  }
  if (displayType === "combined" && timeStamp.isValid) {
    return <>{timeStamp.toFormat(LuxonDateFormats.F)}</>;
  }
  if (displayType === "combined" && displayName != "") {
    return <>{displayName}</>;
  }

  return <>{placeholder}</>;
};

export default PillarTableColumnCreateModified;
